import { useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const norm = (v) => JSON.stringify(v ?? null);
const pendingWrites = new Map();
const notifySync = () => window.dispatchEvent(new Event("protected-sync-status"));
export const hasPendingProtectedSaves = () => pendingWrites.size > 0;
async function runPending(key, task) {
  if (task.running) return task.running;
  clearTimeout(task.timer);
  task.running = (async () => {
    while (task.next) {
      const save = task.next; task.next = null;
      try { await save(); task.error = null; }
      catch (error) { task.error = error; task.next = task.next || save; throw error; }
    }
  })();
  try { await task.running; }
  finally {
    task.running = null;
    if (!task.next && !task.error) pendingWrites.delete(key);
    notifySync();
  }
}
export function queueProtectedSave(key, save, delay = 600) {
  const task = pendingWrites.get(key) || { next: null, timer: null, running: null, error: null };
  task.next = save; pendingWrites.set(key, task); clearTimeout(task.timer); notifySync();
  if (!task.running) task.timer = setTimeout(() => runPending(key, task).catch((e) => toast.error(`Niet opgeslagen (${key}): ${formatApiError(e)}`, { duration: 15000 })), delay);
}
export async function flushProtectedSave(key) {
  const task = pendingWrites.get(key);
  if (task) await runPending(key, task);
}
export async function flushProtectedSaves() {
  await Promise.all([...pendingWrites.entries()].map(([key, task]) => runPending(key, task)));
}


// Mobiele acties schrijven uitsluitend hun gewijzigde records/velden, nooit de hele appdata.
const MOBILE_RECORD_KEYS = new Set(["entries", "halffabRuns", "recepten", "planning", "helpers", "hulpstofInkoop", "rawMaterials", "expeditie", "diversenInkoop"]);
const MOBILE_SETTING_KEYS = new Set(["diversenStam", "diversenVerbruik", "regFormInzendingen", "regFormSjablonen", "bestellijst"]);
export async function saveProtectedChanges(before, after) {
  const writes = [];
  for (const [key, value] of Object.entries(after)) {
    if (norm(before[key]) === norm(value)) continue;
    if (MOBILE_RECORD_KEYS.has(key)) {
      const old = new Map((before[key] || []).map((r) => [String(r.id), r]));
      for (const record of value || []) {
        const base = old.get(String(record.id)) || null;
        if (norm(base) !== norm(record)) writes.push(() => api.put(`/records/${key}/${encodeURIComponent(record.id)}`, { data: record, base_data: base }));
      }
    } else if (MOBILE_SETTING_KEYS.has(key)) {
      writes.push(() => api.put(`/instellingen/${key}`, { value, base_value: before[key] ?? null }));
    } else {
      throw new Error(`Mobiele opslag voor ${key} is niet aangesloten; niets bevestigd.`);
    }
  }
  for (const write of writes) await write();
}

// Per-record synchronisatie naar de beschermde opslag: alleen gewijzigde/nieuwe records worden gePUT, verwijderde zacht geDELETE.
export function useRecordSync(key, items, enabled) {
  const synced = useRef(null);
  const source = useRef(new Map());
  useEffect(() => {
    if (!enabled || synced.current === null) return;
    const map = synced.current;
    const now = new Map();
    (items || []).forEach((r) => { if (r && typeof r === "object" && r.id != null) now.set(String(r.id), r); });
    const changed = [...now.entries()].filter(([id, r]) => map.get(id) !== norm(r));
    const removed = [...map.keys()].filter((id) => !now.has(id));
    if (!changed.length && !removed.length) return;
    changed.forEach(([id, r]) => queueProtectedSave(`record:${key}:${id}`, async () => {
      await api.put(`/records/${key}/${encodeURIComponent(id)}`, { data: r, base_data: source.current.has(id) ? JSON.parse(source.current.get(id)) : null });
      map.set(id, norm(r)); source.current.set(id, norm(r));
    }));
    removed.forEach((id) => queueProtectedSave(`record:${key}:${id}`, async () => {
      await api.delete(`/records/${key}/${encodeURIComponent(id)}`, { data: { base_data: source.current.has(id) ? JSON.parse(source.current.get(id)) : null } });
      map.delete(id); source.current.delete(id);
    }));
  }, [items, enabled, key]);
  const mark = (arr, originals = arr) => {
    const records = (rows) => new Map((rows || []).filter((r) => r && typeof r === "object" && r.id != null).map((r) => [String(r.id), norm(r)]));
    synced.current = records(arr);
    source.current = records(originals);
  };
  return mark;
}

// Maandcijfers (directie): per maand-sleutel.
export function useMaandSync(maandData, enabled) {
  const synced = useRef(null);
  useEffect(() => {
    if (!enabled || synced.current === null) return;
    const map = synced.current;
    const changed = Object.entries(maandData || {}).filter(([k, v]) => map.get(k) !== norm(v));
    if (!changed.length) return;
    changed.forEach(([k, v]) => queueProtectedSave(`maand:${k}`, async () => {
      await api.put(`/maanddata/${encodeURIComponent(k)}`, { data: v, base_data: map.has(k) ? JSON.parse(map.get(k)) : null });
      map.set(k, norm(v));
    }));
  }, [maandData, enabled]);
  return (md) => { const m = new Map(); Object.entries(md || {}).forEach(([k, v]) => m.set(k, norm(v))); synced.current = m; };
}

// Overige velden (rechten, samples, specs, leveranciers, …): per veld één beschermd document met historie.
export function useSettingsSync(map, enabled) {
  const synced = useRef(null);
  const values = Object.values(map);
  useEffect(() => {
    if (!enabled || synced.current === null) return;
    const s = synced.current;
    const changed = Object.entries(map).filter(([k, v]) => v !== undefined && s.get(k) !== norm(v));
    if (!changed.length) return;
    changed.forEach(([k, v]) => queueProtectedSave(`instelling:${k}`, async () => {
      await api.put(`/instellingen/${k}`, { value: v, base_value: s.has(k) ? JSON.parse(s.get(k)) : null });
      s.set(k, norm(v));
    }, 800));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...values, enabled]);
  const mark = (d) => { const m = synced.current || new Map(); Object.keys(map).forEach((k) => { if (d[k] !== undefined) m.set(k, norm(d[k])); }); synced.current = m; };
  mark.field = (key, value) => synced.current?.set(key, norm(value));
  return mark;
}

export function useCostsSync(costs, costsHistory, enabled) {
  const synced = useRef(null);
  useEffect(() => {
    if (!enabled || synced.current === null) return;
    const s = synced.current;
    const body = {};
    if (s.costs !== norm(costs)) body.costs = costs;
    if (s.costsHistory !== norm(costsHistory)) body.costsHistory = costsHistory;
    if (!Object.keys(body).length) return;
    queueProtectedSave("kosten", async () => {
      const update = {};
      if (s.costs !== norm(costs)) Object.assign(update, { costs, base_costs: JSON.parse(s.costs) });
      if (s.costsHistory !== norm(costsHistory)) Object.assign(update, { costsHistory, base_costsHistory: JSON.parse(s.costsHistory) });
      if (!Object.keys(update).length) return;
      await api.put("/instellingen/costs", update);
      if (update.costs) s.costs = norm(costs);
      if (update.costsHistory) s.costsHistory = norm(costsHistory);
    });
  }, [costs, costsHistory, enabled]);
  return (c, h) => { synced.current = { costs: norm(c), costsHistory: norm(h) }; };
}
